import { components, operations } from "../../api/types";
import enums from "../../lang/en/enums.json";

export type CaseStatus = components["schemas"]["CaseStatus"];
export type CaseInformationAccidentType = components["schemas"]["CaseInformationAccidentType"];
export type CaseInformationNatureOfInjury = components["schemas"]["CaseInformationNatureOfInjury"];

export type Page = "Dashboard" | "Documents" | "Revisions";
export type CaseInformationResolutionType =
  components["schemas"]["CaseInformationResolutionType"];
export type CaseResultType = components["schemas"]["CaseInformationResult"];
export const literalCaseStatus = (status: CaseStatus): string => {
  if (enums.case_statuses[status]) {
    return enums.case_statuses[status];
  }
  throw new Error(`Unknown case status: ${status}`);
};
export const literalCaseInformationAccidentType = (accident_type: CaseInformationAccidentType): string => {
  if (enums.case_information_accident_types[accident_type]) {
    return enums.case_information_accident_types[accident_type];
  }
  throw new Error(`Unknown case status: ${accident_type}`);
};
export const literalInformationNatureOfInjury = (nature_injury: CaseInformationNatureOfInjury): string => {
  if (enums.case_information_nature_of_injury[nature_injury]) {
    return enums.case_information_nature_of_injury[nature_injury];
  }
  throw new Error(`Unknown case status: ${nature_injury}`);
};
export const resolutionTypeColorMap: Record<
  CaseInformationResolutionType,
  { bg: string; text: string }
> = {
  settlement: { bg: "#ACF2C8", text: "#266A4A" },
  dismissal: { bg: "#00796B", text: "#ffffff" },
  trial: { bg: "#00796B", text: "#ffffff" },
  summary_judgment: {bg: "#00796B", text: "#ffffff"}
};

export const caseResultColorMap: Record<
  CaseResultType,
  { bg: string; text: string }
> = {
  resolved: { bg: "rgba(124, 77, 255, 0.04)", text: "#B388FF" },
  plaintiff: { bg: "#00796B14", text: "#B388FF" },
  defendant: { bg: "#00796B14", text: "#2E7D32" },
};

export const caseStatusColorMap: Record<
  CaseStatus,
  { bg: string; text: string }
> = {
  active: { bg: "#ACF2C8", text: "#65558F" },
  processing: { bg: "#6750A4", text: "white" },
  archived: { bg: "#616161", text: "white" },
  error: { bg: "rgba(211, 47, 47, 0.30)", text: "#D32F2F" },
};

export interface TableFilters {
  resolutionType: CaseInformationResolutionType | null;
  caseResult: CaseResultType | null;
}

export type CaseMeta = components["schemas"]["CaseMeta"];
export type CaseOutcome = components["schemas"]["CaseOutcome"];
export type RelatedCaseRow = components["schemas"]["RevisionCaseNeighbor"];
export type CaseFile = components["schemas"]["CaseFile"];
export type PresignedFileUrl = components["schemas"]["PresignedFileUrl"];
export type RevisionFilesPresignResponse =
  components["schemas"]["RevisionFilesPresignResponse"];

export type CreateCaseResponse =
  operations["create_case_v1_cases__post"]["responses"][201]["content"]["application/json"];

export type CreateCaseRevisionResponse =
  operations["create_revision_v1_cases__case_id__revisions__post"]["responses"][201]["content"]["application/json"];

export type CreateFilesResponse =
  operations["upload_case_files_v1_cases__case_id__revisions__revision_id__uploads__post"]["responses"][201]["content"]["application/json"];
export type CreateFilesRequest =
  components["schemas"]["Body_upload_case_files_v1_cases__case_id__revisions__revision_id__uploads__post"];
export type CreateCaseRequest = components["schemas"]["CreateCaseRequest"];
export type CreateCaseRevisionRequest =
  components["schemas"]["CreateCaseRevisionRequest"];

export type CaseRevisionMeta = components["schemas"]["CaseRevisionMeta"];

export type TableSortDirection = "asc" | "desc";
export type TableSortConfig = {
  key: string;
  direction: TableSortDirection;
};

export interface TableColumn<T> {
  id: string;
  label: string;
  sortable?: boolean;
  getValue?: (row: T) => any;
  columnWidth?: string | number;
  textAlign?: "left" | "right" | "center";
  backgroundColor?: string;
}

export type OutcomeResponse = components["schemas"]["CaseOutcome"];
export type RevisionExtermalStatus =
  components["schemas"]["RevisionExternalStatus"];

/**
 * Upload-subsystem error codes emitted by the backend.
 * Mirrors `app/core/exceptions/uploads.py::UploadErrorCode` — keep in sync.
 */
export const UPLOAD_ERROR_CODES = [
  "UPLOAD_SESSION_GENERAL",
  "COMPLETION_INVARIANT",
  "REVISION_NOT_INITIAL",
  "REVISION_HAS_FILES",
  "REVISION_NOT_FOUND",
  "CHUNK_INVALID",
  "COPY_FAILED",
  "REASSEMBLY_FAILED",
  "START_SESSION_FAILED",
  "FILE_TOO_LARGE",
  "FILE_EMPTY",
  "INVALID_CONTENT_TYPE",
  "INVALID_EXTENSION",
  "INVALID_CONTENT",
  "TOO_MANY_FILES",
  "NO_FILES_PROVIDED",
  "UPLOAD_SIZE_EXCEEDED",
  "UPLOAD_INTEGRITY_SIZE",
  "UPLOAD_SESSION_EXPIRED",
  // v2 upload error codes
  "UPLOAD_TEMP_BLOB_MISSING",
  "ZIP_EXTRACTION_FAILED",
  "ZIP_EMPTY",
  "ZIP_NESTED_NOT_SUPPORTED",
  "ZIP_CONTAINS_INVALID_FILES",
  "SIZE_MISMATCH",
  "ZERO_ACCEPTED_ITEMS",
  "STORAGE_COPY_FAILED",
  "CASE_NOT_FOUND",
  "CASE_FORBIDDEN",
  "TOO_MANY_REJECTED_ITEMS",
] as const;

export type UploadErrorCode = (typeof UPLOAD_ERROR_CODES)[number];

export const UPLOAD_ERROR_CODE_SET: ReadonlySet<string> = new Set(
  UPLOAD_ERROR_CODES
);

/** Backend error code returned when finalize has no files to process. */
export const BACKEND_ERROR_NO_FILES_PROVIDED: UploadErrorCode =
  "NO_FILES_PROVIDED";

// exceptions
export class FileUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FileUploadError";
  }
}
export class CreateRevisionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CreateRevisionError";
  }
}


export class CreateCaseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CreateCaseError";
  }
}